import React, { useState, useContext, useMemo } from "react";
import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import {
  FaSearch,
  FaFilter,
  FaTimes,
  FaDownload,
  FaTools,
  FaRecycle,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";

const UnderMaintenanceScrapReport = () => {
  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryButtonClass = `bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 text-white`;
  const secondaryButtonClass = "bg-gray-200 hover:bg-gray-300 text-gray-800";

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  // LocalStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "8";
  const branchType = (localStorage.getItem("branchType") || "HO").toUpperCase();
  const userId = localStorage.getItem("userId") || "";
  const loginUserId = localStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
      "x-loginuserid": loginUserId,
    }),
    [finYearId, finYear, branchId, branchType, userId, loginUserId]
  );

  // Form state
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [itemCode, setItemCode] = useState("");
  const [itemName, setItemName] = useState("");

  // Data state
  const [data, setData] = useState([]);

  const formatDate = (dateValue) => {
    if (!dateValue) return "-";

    // Handles dd/mm/yyyy coming from API
    if (typeof dateValue === "string" && dateValue.includes("/")) {
      return dateValue;
    }

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return dateValue;

    return date.toLocaleDateString("en-IN");
  };

  const handleSearch = async () => {
    if (!fromDate || !toDate) {
      toast.warning("Please select both From Date and To Date");
      return;
    }

    setLoading(true);

    const params = {
      branchid: branchId,
      fromDate: fromDate,
      todate: toDate,
      itemcode: itemCode || "",
      itemname: itemName || "",
    };

    try {
      const res = await axios.get(
        "http://nissi-india.com/api/underMaintenanceScrap_rpt",
        {
          params,
          headers: commonHeaders,
        }
      );

      const list = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
        ? res.data.data
        : [];

      setData(list);
      toast.success(`Loaded ${list.length} records`);
    } catch (err) {
      console.error("Maintenance Scrap fetch failed", err);
      toast.error("Failed to fetch maintenance scrap listing");
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFromDate("");
    setToDate("");
    setItemCode("");
    setItemName("");
    setData([]);
  };

  const handleExportCSV = () => {
    if (!data.length) {
      toast.warning("No data available to export");
      return;
    }

    const headers = [
      "S.No",
      "Item Code",
      "Item Name",
      "UOM",
      "Removed From",
      "Removed Date",
      "DC Date",
      "Remarks",
      "Qty",
    ];

    const rows = data.map((item, index) => [
      index + 1,
      item.fld_itemcode || "",
      item.fld_itemname || "",
      item.fld_uomcode || "",
      item.fld_removedfrom || "",
      item.fld_removeddate || "",
      item.fld_dcdate || "",
      item.fld_remarks || "",
      item.fld_qty || 0,
    ]);

    const csvContent = [headers, ...rows]
      .map((row) =>
        row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "under_maintenance_scrap_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("CSV exported successfully");
  };

  return (
    <LayoutWrapper>
      <HeaderBar title="UNDER MAINTENANCE SCRAP REPORT" />
      <ToastContainer />

      <div
        className={`p-6 mt-6 rounded-xl shadow-sm border ${
          isDark
            ? "bg-gray-800 border-gray-700 text-white"
            : "bg-white border-gray-200 text-gray-900"
        }`}
      >
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold">Maintenance Scrap Report</h1>
            <p className="text-sm text-gray-500 mt-1">
              Track and manage under maintenance scrap items
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                showFilters ? primaryButtonClass : secondaryButtonClass
              }`}
            >
              <FaFilter className="w-4 h-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>

            <button
              onClick={handleExportCSV}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg ${secondaryButtonClass}`}
            >
              <FaDownload className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* Filters Section */}
        {showFilters && (
          <div
            className={`rounded-lg p-5 mb-6 ${
              isDark ? "bg-gray-700" : "bg-gray-50"
            }`}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Search Filters</h3>
              <button
                onClick={clearFilters}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <FaTimes className="w-3 h-3" /> Clear all
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
              <div>
                <label className="block text-sm font-medium mb-2">
                  From Date *
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300 text-gray-900"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  To Date *
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300 text-gray-900"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Item Code
                </label>
                <input
                  type="text"
                  value={itemCode}
                  onChange={(e) => setItemCode(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300 text-gray-900"
                  placeholder="Enter item code"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Item Name
                </label>
                <input
                  type="text"
                  value={itemName}
                  onChange={(e) => setItemName(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300 text-gray-900"
                  placeholder="Enter item name"
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-end pt-4 border-t border-gray-200">
              <button
                onClick={handleSearch}
                className={`flex items-center gap-2 ${primaryButtonClass} px-4 py-2 rounded transition-colors`}
              >
                <FaSearch className="w-4 h-4" />
                Search
              </button>
            </div>
          </div>
        )}

        {/* Results Section */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">
              Results {data.length > 0 && `(${data.length} records)`}
            </h2>

            {data.length > 0 && (
              <span className="text-sm text-gray-500">
                Last updated: {new Date().toLocaleTimeString()}
              </span>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : data.length > 0 ? (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className={isDark ? "bg-gray-700" : "bg-gray-50"}>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                      S.No
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                      Item Code
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                      Item Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                      UOM
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                      Removed From
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                      Removed Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                      DC Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                      Remarks
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider">
                      Qty
                    </th>
                  </tr>
                </thead>

                <tbody
                  className={`divide-y ${
                    isDark
                      ? "bg-gray-800 divide-gray-700"
                      : "bg-white divide-gray-200"
                  }`}
                >
                  {data.map((item, index) => (
                    <tr
                      key={`${item.fld_itemcode}-${index}`}
                      className={isDark ? "hover:bg-gray-700" : "hover:bg-gray-50"}
                    >
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        {index + 1}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                        {item.fld_itemcode || "-"}
                      </td>
                      <td className="px-4 py-4 text-sm">
                        {item.fld_itemname || "-"}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        {item.fld_uomcode || "-"}
                      </td>
                      <td className="px-4 py-4 text-sm">
                        {item.fld_removedfrom || "-"}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        {formatDate(item.fld_removeddate)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        {formatDate(item.fld_dcdate)}
                      </td>
                      <td className="px-4 py-4 text-sm">
                        {item.fld_remarks || "-"}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-right font-semibold">
                        {item.fld_qty ?? 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div
              className={`text-center py-12 rounded-lg border border-dashed ${
                isDark
                  ? "bg-gray-700 border-gray-600"
                  : "bg-gray-50 border-gray-300"
              }`}
            >
              <div className="flex justify-center">
                <FaTools className="h-12 w-12 text-gray-400 mr-4" />
                <FaRecycle className="h-12 w-12 text-gray-400" />
              </div>
              <h3 className="mt-2 text-sm font-medium">
                No maintenance scrap records
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by searching with the filters above.
              </p>
            </div>
          )}
        </div>
      </div>
    </LayoutWrapper>
  );
};

export default UnderMaintenanceScrapReport;